import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '@/lib/api';
import { toast } from 'sonner';
import { KpiCard } from '../common/KpiCard';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Skeleton } from '../ui/skeleton';
import { formatCurrency } from '../../lib/formatters';
import { AlertTriangle, IndianRupee, CreditCard, Gift, RefreshCw, FileX, ArrowRight, Lock, PlusCircle } from 'lucide-react';

export default function CollectionsManagerDashboard() {
  const navigate = useNavigate();
  const [demandSummary, setDemandSummary] = useState(null);
  const [restructureSummary, setRestructureSummary] = useState(null);
  const [writeOffSummary, setWriteOffSummary] = useState(null);
  const [refundSummary, setRefundSummary] = useState(null);
  const [closedLoans, setClosedLoans] = useState(0);
  const [newLoans, setNewLoans] = useState(0);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      api.get('/api/demands/summary'),
      api.get('/api/recovery/restructures/summary').catch(() => null),
      api.get('/api/recovery/write-offs/summary').catch(() => null),
      api.get('/api/recovery/refunds/summary').catch(() => null),
      api.get('/api/dashboard/stats').catch(() => null),
    ]).then(([demand, restructure, writeOff, refund, stats]) => {
      setDemandSummary(demand);
      setRestructureSummary(restructure);
      setWriteOffSummary(writeOff);
      setRefundSummary(refund);
      setClosedLoans(stats?.closed_loans || 0);
      setNewLoans(stats?.new_loans || 0);
    }).catch(e => toast.error(e.message || 'Failed to load collections data')).finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <div className="space-y-6" data-testid="collections-manager-dashboard">
        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
          {['s1','s2','s3','s4'].map(id => <Skeleton key={id} className="h-32 rounded-xl" />)}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="collections-manager-dashboard">
      <div className="gradient-header -mx-4 md:-mx-6 lg:-mx-8 -mt-6 px-4 md:px-6 lg:px-8 py-6 mb-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-semibold tracking-tight">Collections Dashboard</h1>
            <p className="text-muted-foreground mt-1">Overdue recovery and collections performance</p>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => navigate('/demands/overdue')} data-testid="go-to-overdue-btn">
              <AlertTriangle className="h-4 w-4 mr-2" />Overdue Loans
            </Button>
            <Button onClick={() => navigate('/demands/payments')} data-testid="go-to-payments-btn">
              <CreditCard className="h-4 w-4 mr-2" />Apply Payment
            </Button>
          </div>
        </div>
      </div>

      {/* Primary KPIs - Demands */}
      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-5 gap-4">
        <KpiCard title="Total Outstanding" value={formatCurrency(demandSummary?.total_outstanding || 0)} icon={IndianRupee} data-testid="col-kpi-outstanding" />
        <KpiCard title="Overdue Demands" value={demandSummary?.total_overdue || 0} icon={AlertTriangle} variant={demandSummary?.total_overdue > 0 ? 'danger' : 'default'} data-testid="col-kpi-overdue" />
        <KpiCard title="Total Collected" value={formatCurrency(demandSummary?.total_paid_amount || 0)} icon={CreditCard} variant="success" data-testid="col-kpi-collected" />
        <KpiCard title="Total Waived" value={formatCurrency(demandSummary?.total_waived || 0)} icon={Gift} data-testid="col-kpi-waived" />
        <KpiCard title="Closed Loans" value={closedLoans} icon={Lock} data-testid="col-kpi-closed-loans" />
        <KpiCard title="New Loans" value={newLoans} icon={PlusCircle} variant="success" data-testid="col-kpi-new-loans" />
      </div>

      {/* Secondary KPIs - Recovery */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <Card className="rounded-xl border shadow-sm p-4 md:p-5 card-interactive">
          <div className="flex items-start justify-between">
            <div className="flex-1">
              <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Restructures</p>
              <p className="mt-2 text-2xl font-semibold tracking-tight tabular-nums">{restructureSummary?.total_restructures || 0}</p>
              <p className="mt-1 text-xs text-amber-600">{restructureSummary?.draft_count ?? restructureSummary?.pending_count ?? 0} pending</p>
            </div>
            <div className="p-2 rounded-lg bg-muted"><RefreshCw className="h-5 w-5 text-muted-foreground" /></div>
          </div>
        </Card>
        <Card className="rounded-xl border shadow-sm p-4 md:p-5 card-interactive">
          <div className="flex items-start justify-between">
            <div className="flex-1">
              <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Write-Offs</p>
              <p className="mt-2 text-2xl font-semibold tracking-tight tabular-nums">{writeOffSummary?.total_write_offs || 0}</p>
              <p className="mt-1 text-xs text-amber-600">{writeOffSummary?.pending_count || 0} pending</p>
            </div>
            <div className="p-2 rounded-lg bg-muted"><FileX className="h-5 w-5 text-muted-foreground" /></div>
          </div>
        </Card>
        <Card className="rounded-xl border shadow-sm p-4 md:p-5 card-interactive">
          <div className="flex items-start justify-between">
            <div className="flex-1">
              <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Refunds</p>
              <p className="mt-2 text-2xl font-semibold tracking-tight tabular-nums">{refundSummary?.total_refunds || 0}</p>
              <p className="mt-1 text-xs text-amber-600">{refundSummary?.pending_count || 0} pending</p>
            </div>
            <div className="p-2 rounded-lg bg-muted"><IndianRupee className="h-5 w-5 text-muted-foreground" /></div>
          </div>
        </Card>
      </div>

      {/* Quick Links */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Quick Actions</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {[
              { label: 'Loan Demands', href: '/demands', icon: IndianRupee },
              { label: 'Waiver Processing', href: '/demands/waivers', icon: Gift },
              { label: 'Restructures', href: '/recovery/restructures', icon: RefreshCw },
              { label: 'Write-Offs', href: '/recovery/write-offs', icon: FileX },
            ].map(item => (
              <Button key={item.href} variant="outline" className="h-auto py-3 flex flex-col items-center gap-1.5" onClick={() => navigate(item.href)} data-testid={`quick-${item.label.toLowerCase().replace(/\s/g, '-')}`}>
                <item.icon className="h-5 w-5" />
                <span className="text-xs">{item.label}</span>
                <ArrowRight className="h-3 w-3 text-muted-foreground" />
              </Button>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
